import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Chip
} from '@mui/material';
import { NotificationsActive, CheckCircle, Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { apiRequest } from '../api/api';
import { getApiErrorMessage } from '../utils/apiError';

dayjs.locale('it');

// Ruoli per cui il popup deve essere mostrato
const RUOLI_TARGET = ['dipendente', 'linker', 'maker', 'referente'];

// Codici che richiedono conferma orari (da MioOrarioCel)
const CODICI_OBBLIGO_CONSOLIDAMENTO = [
  'LAVORATO',
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING',
];

interface TurnoNonConfermato {
  id: number;
  data: string;
  turno: string;
  codiceTipoOrario?: string;
  oraPrevInizio: string;
  oraPrevFine: string;
  oraConsInizio: string;
  oraConsFine: string;
  // campi di input per la conferma
  inputConsInizio: string;
  inputConsFine: string;
  confermato: boolean;
  errore?: string;
}

/**
 * Normalizza un valore ora (es. "08:00:00" → "08:00", "0" → "", null → "")
 */
const normalizeOra = (ora: string | null | undefined): string => {
  if (!ora) return '';
  const s = ora.trim();
  if (s === '0' || s === '00:00:00' || s === '00:00') return '';
  // HH:mm:ss → HH:mm
  if (s.length === 8 && s[2] === ':' && s[5] === ':') return s.substring(0, 5);
  return s;
};

const normalizeOraConsuntivo = (ora: string | null | undefined): string => {
  if (!ora) return '';
  const s = ora.trim();
  if (s === '0') return '';
  if (s.length === 8 && s[2] === ':' && s[5] === ':') return s.substring(0, 5);
  return s;
};

/**
 * Restituisce true se il campo ora è "non impostato" (null, vuoto, "0", "00:00")
 */
const isOraVuota = (ora: string | null | undefined): boolean => {
  if (!ora) return true;
  const s = ora.trim();
  return s === '' || s === '0' || s === '00:00:00' || s === '00:00';
};

/**
 * Per il consuntivo "00:00" è un orario valido (es. ROL 00:00-06:00).
 */
const isOraConsuntivoVuota = (ora: string | null | undefined): boolean => {
  if (!ora) return true;
  const s = ora.trim();
  return s === '' || s === '0';
};

/**
 * Verifica se il turno è già terminato (data + oraPrevFine < ora attuale)
 */
const isTurnoTerminato = (data: string, oraPrevFine: string): boolean => {
  const fine = normalizeOra(oraPrevFine);
  if (!fine) return false;
  const [hh, mm] = fine.split(':').map(Number);
  const fineDateTime = dayjs(data).hour(hh).minute(mm).second(0);
  return dayjs().isAfter(fineDateTime);
};

/**
 * Verifica se un turno è da confermare
 */
const isDaConfermare = (t: any): boolean => {
  // Deve avere orari previsti significativi
  const prevInizioOk = !isOraVuota(t.oraPrevInizio);
  const prevFineOk = !isOraVuota(t.oraPrevFine);
  if (!prevInizioOk && !prevFineOk) return false;

  // Deve richiedere consolidamento obbligatorio
  if (t.codiceTipoOrario && !CODICI_OBBLIGO_CONSOLIDAMENTO.includes(t.codiceTipoOrario)) return false;

  // Non deve essere già confermato
  const consInizioVuoto = isOraConsuntivoVuota(t.oraConsInizio);
  const consFineVuota = isOraConsuntivoVuota(t.oraConsFine);
  if (!consInizioVuoto && !consFineVuota) return false;

  // Il turno deve essere già terminato
  if (!isTurnoTerminato(t.data, t.oraPrevFine)) return false;

  return true;
};

/**
 * True se il turno appartiene a un mese diverso da quello corrente
 * (es. a giugno 2026, restituisce true per maggio 2026, aprile 2026 e prima)
 * I turni del mese corrente sono confermabili, gli altri no
 */
const isTurnoVecchio = (data: string): boolean => {
  const inizioMeseCorrente = dayjs().startOf('month');
  return dayjs(data).isBefore(inizioMeseCorrente, 'month');
};

/**
 * Verifica se esiste un turno non confermato precedente a quello passato
 * Include turni del mese precedente (ma non di mesi ancora più vecchi)
 */
const hasPreviousUnconfirmed = (turno: TurnoNonConfermato, turniList: TurnoNonConfermato[]): boolean => {
  const inizioMesePrecedente = dayjs().subtract(1, 'month').startOf('month');
  
  return turniList.some(t => {
    const tDate = dayjs(t.data);
    const turnoDate = dayjs(turno.data);
    
    // Non confermato
    if (t.confermato) return false;
    
    // Data precedente al turno
    if (!tDate.isBefore(turnoDate, 'day')) return false;
    
    // Deve essere nel mese corrente o nel mese precedente (non più indietro)
    if (tDate.isBefore(inizioMesePrecedente, 'day')) return false;
    
    return true;
  });
};

/**
 * Mostrato una volta per sessione al dipendente/linker/maker/referente
 * se esistono turni passati con orari previsti ma privi di consolidamento.
 */
const TurnoNonConfermatoPopup: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [turni, setTurni] = useState<TurnoNonConfermato[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [allConfermati, setAllConfermati] = useState(false);
  const [lastUserKey, setLastUserKey] = useState<string>('');

  // Estrai la logica di fetch in una funzione separata
  const fetchTurniNonConfermati = async (userInfo: any) => {
    const ruolo = (userInfo.ruolo || '').toLowerCase();
    if (!RUOLI_TARGET.includes(ruolo)) return;

    const codicePersonale = userInfo.oldId || '';
    if (!codicePersonale) return;

    setLoading(true);

    try {
      const dataFine = dayjs().format('YYYY-MM-DD');
      const dataInizio = dayjs().subtract(90, 'day').format('YYYY-MM-DD');

      const data = await apiRequest<any[]>(
        `/orari-generati/codice/${encodeURIComponent(codicePersonale)}?dataInizio=${dataInizio}&dataFine=${dataFine}`
      );

      const nonConfermati = (data || [])
        .filter(isDaConfermare)
        .map((t): TurnoNonConfermato => ({
          id: t.id,
          data: t.data,
          turno: t.turno || '',
          codiceTipoOrario: t.codiceTipoOrario || '',
          oraPrevInizio: t.codiceTipoOrario === 'ROL' ? normalizeOraConsuntivo(t.oraPrevInizio) : normalizeOra(t.oraPrevInizio),
          oraPrevFine: normalizeOra(t.oraPrevFine),
          oraConsInizio: t.oraConsInizio || '',
          oraConsFine: t.oraConsFine || '',
          inputConsInizio: t.codiceTipoOrario === 'ROL' ? normalizeOraConsuntivo(t.oraPrevInizio) : normalizeOra(t.oraPrevInizio),
          inputConsFine: normalizeOra(t.oraPrevFine),
          confermato: false,
        }))
        .sort((a, b) => a.data.localeCompare(b.data));

      if (nonConfermati.length > 0) {
        setTurni(nonConfermati);
        setOpen(true);
      }
    } catch (err) {
      console.error('[TurnoNonConfermatoPopup] Errore fetch turni:', err);
    } finally {
      setLoading(false);
    }
  };

  // Monitora i cambi di userInfo (login/logout)
  useEffect(() => {
    const checkUserInfo = () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const userKey = userInfo.oldId || '';
        
        // Se userKey è diverso dal precedente (login, logout, cambio utente), fetchTurni
        if (userKey && userKey !== lastUserKey) {
          setLastUserKey(userKey);
          fetchTurniNonConfermati(userInfo);
        }
      } catch (err) {
        console.error('[TurnoNonConfermatoPopup] Errore lettura userInfo:', err);
      }
    };

    // Check iniziale al mount
    checkUserInfo();

    // Monitora i cambi di localStorage (da altre tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userInfo' || e.key === null) {
        checkUserInfo();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Interval per controllare periodicamente (fallback per cambi nello stesso tab)
    const interval = setInterval(checkUserInfo, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [lastUserKey]);

  const handleInputChange = (id: number, field: 'inputConsInizio' | 'inputConsFine', value: string) => {
    setTurni(prev =>
      prev.map(t => t.id === id ? { ...t, [field]: value, errore: undefined } : t)
    );
  };

  const handleConferma = async (id: number) => {
    const turno = turni.find(t => t.id === id);
    if (!turno) return;

    if (!turno.inputConsInizio || !turno.inputConsFine) {
      setTurni(prev =>
        prev.map(t => t.id === id ? { ...t, errore: 'Inserisci orario inizio e fine' } : t)
      );
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('oraConsInizio', turno.inputConsInizio);
      params.append('oraConsFine', turno.inputConsFine);
      await apiRequest(`/orari-generati/${id}/consuntivo?${params.toString()}`, { method: 'PUT' });

      setTurni(prev => {
        const updated = prev.map(t =>
          t.id === id ? { ...t, confermato: true, errore: undefined } : t
        );
        setAllConfermati(updated.every(t => t.confermato));
        return updated;
      });
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Errore nella conferma. Riprova più tardi.');
      setTurni(prev =>
        prev.map(t => t.id === id ? { ...t, errore: msg } : t)
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfermaAll = async () => {
    setSaving(true);
    setGlobalError(null);
    // Considera solo i turni che possono essere confermati (non vecchi)
    const pending = turni.filter(t => !t.confermato && !isTurnoVecchio(t.data));
    for (const turno of pending) {
      if (!turno.inputConsInizio || !turno.inputConsFine) {
        setTurni(prev =>
          prev.map(t => t.id === turno.id ? { ...t, errore: 'Inserisci orario inizio e fine' } : t)
        );
        setSaving(false);
        return;
      }
    }
    let hasError = false;
    for (const turno of pending) {
      try {
        const params = new URLSearchParams();
        params.append('oraConsInizio', turno.inputConsInizio);
        params.append('oraConsFine', turno.inputConsFine);
        await apiRequest(`/orari-generati/${turno.id}/consuntivo?${params.toString()}`, { method: 'PUT' });
        setTurni(prev =>
          prev.map(t => t.id === turno.id ? { ...t, confermato: true, errore: undefined } : t)
        );
      } catch (err: any) {
        hasError = true;
        const msg = getApiErrorMessage(err, 'Errore nella conferma.');
        setTurni(prev =>
          prev.map(t => t.id === turno.id ? { ...t, errore: msg } : t)
        );
      }
    }
    setSaving(false);
    if (!hasError) {
      setTurni(prev => {
        const allDone = prev.every(t => t.confermato);
        setAllConfermati(allDone);
        return prev;
      });
    }
  };

  if (!open) return null;

  const pendingCount = turni.filter(t => !t.confermato).length;
  const pendingLabel = pendingCount === 1 ? 'turno passato' : 'turni passati';
  const pendingVerb = pendingCount === 1 ? 'non è stato confermato' : 'non sono stati confermati';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsActive color="warning" />
        <Typography variant="h6" component="span">
          Turni da confermare
        </Typography>
        {!allConfermati && (
          <Chip
            label={`${pendingCount} in sospeso`}
            color="warning"
            size="small"
            sx={{ ml: 'auto' }}
          />
        )}
        <IconButton size="small" onClick={handleClose} sx={{ ml: allConfermati ? 'auto' : 0 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : allConfermati ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1 }}>
            <CheckCircle color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6" color="success.main">
              Tutti i turni sono stati confermati!
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Alert severity="warning">
              Hai {pendingCount} {pendingLabel} che {pendingVerb}.
              Inserisci gli orari effettivi e conferma.
            </Alert>

            {globalError && (
              <Alert severity="error">{globalError}</Alert>
            )}

            {turni.map((turno, idx) => (
              <Box key={turno.id}>
                {idx > 0 && <Divider sx={{ mb: 2 }} />}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {dayjs(turno.data).locale('it').format('dddd D MMMM YYYY')}
                    </Typography>
                    {turno.confermato ? (
                      <Chip icon={<CheckCircle />} label="Confermato" color="success" size="small" />
                    ) : (
                      <Chip label={turno.codiceTipoOrario || turno.turno || 'Turno'} size="small" variant="outlined" />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Orario previsto: <strong>{turno.oraPrevInizio} – {turno.oraPrevFine}</strong>
                  </Typography>

                  {!turno.confermato && (
                    isTurnoVecchio(turno.data) ? (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Turno del mese precedente, contattare Coordinatrice
                      </Typography>
                    ) : hasPreviousUnconfirmed(turno, turni) ? (
                      <Typography variant="body2" color="error" fontStyle="italic">
                        Devi confermare prima i turni precedenti
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <TextField
                            label="Inizio effettivo"
                            type="time"
                            size="small"
                            value={turno.inputConsInizio}
                            onChange={(e) => handleInputChange(turno.id, 'inputConsInizio', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 60 }}
                            sx={{ flex: 1 }}
                            disabled={saving}
                          />
                          <TextField
                            label="Fine effettiva"
                            type="time"
                            size="small"
                            value={turno.inputConsFine}
                            onChange={(e) => handleInputChange(turno.id, 'inputConsFine', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 60 }}
                            sx={{ flex: 1 }}
                            disabled={saving}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleConferma(turno.id)}
                            disabled={saving}
                            sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                          >
                            Conferma
                          </Button>
                        </Box>
                        {turno.errore && (
                          <Alert severity="error" sx={{ py: 0 }}>{turno.errore}</Alert>
                        )}
                      </>
                    )
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="success">
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TurnoNonConfermatoPopup;
